import React from 'react';
import { Clipboard, ClipboardCheck } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { ColumnHeaderCycleSort } from '@companion-professional/components';

import {
  CheckupRequestStatusResponse,
  LocationCheckupTags,
  onlyCheckupTags,
  SurveyInfo
} from '@companion-professional/dctypes';
import { cn } from '@companion-professional/webutils';
import { StatusIcon } from './StatusIcon';
import { CheckupActionRowMenu } from './CheckupActionRowMenu';
import { AnalysisIcon } from './AnalysisIcon';
import { TagBadges } from './TagBadges';
import { analysisPrioritySort } from './analysisPrioritySort';
import { HoverLabelCell } from './HoverLabelCell';

// getCheckupColumns returns the react-table columns that should be used to render the checkup table.
export const getCheckupColumns = (
  // onOpen is a function that is called when a user clicks on a row in the table. It should navigate the user to the
  // checkup details page.  inNewTab is a boolean that determines if the checkup should be opened in a new tab.
  onOpen: (checkup: CheckupRequestStatusResponse, inNewTab?: boolean) => void,

  // openQRCodeDialog is a function that is called when a user clicks on the QR code icon in the table. It should open a
  // dialog that shows the QR code for the checkup.
  openQRCodeDialog: (checkup: CheckupRequestStatusResponse) => void,

  // enableAnalysisColumn is a boolean that determines whether the user is shown the analysis column and filter.
  enableAnalysisColumn?: boolean,

  surveys?: SurveyInfo[]
): ColumnDef<CheckupRequestStatusResponse>[] => {
  const columns: ColumnDef<CheckupRequestStatusResponse>[] = [
    {
      accessorKey: 'status',
      header: () => <div className="flex justify-center align-middle">Status</div>,
      cell: ({ row }) => (
        <div className="flex justify-center align-middle" onClick={() => onOpen(row.original)}>
          <StatusIcon status={row.getValue('status')} />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      filterFn: (row, id, value) => value.includes(row.getValue(id))
    }
  ];

  // Add the analysis column if it should be enabled.
  if (enableAnalysisColumn) {
    columns.push({
      accessorKey: 'analysis_status',
      header: ({ column }) => (
        <div className="flex justify-center align-middle">
          <ColumnHeaderCycleSort column={column} title="Analysis" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center align-middle" onClick={() => onOpen(row.original)}>
          <AnalysisIcon status={row.getValue('analysis_status')} />
        </div>
      ),
      enableHiding: false,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
      sortingFn: (rowA, rowB) => {
        return analysisPrioritySort(rowA, rowB);
      }
    });
  }

  if (surveys && surveys.length > 0) {
    surveys.forEach((survey) => {
      columns.push({
        accessorKey: `survey_${survey.survey_id}`,
        accessorFn: (row) =>
          row?.answered_surveys?.some((s) => s.survey_id === survey.survey_id) ? 'answered' : 'not_started',
        header: () => <div className="flex w-16 justify-center text-center align-middle">{survey.title}</div>,
        cell: ({ row }) => {
          return (
            <div className="flex w-16 justify-center align-middle" onClick={() => onOpen(row.original)}>
              {row.original?.answered_surveys?.some((s) => s.survey_id === survey.survey_id) ? (
                // Has answered the survey
                <ClipboardCheck size={18} className="text-primary" />
              ) : (
                // Not answered: highlighted as needing attention once the checkup is complete, otherwise muted.
                <Clipboard
                  size={18}
                  className={cn({
                    'text-destructive': row.original?.status === 'complete',
                    'text-muted-foreground': row.original?.status !== 'complete'
                  })}
                />
              )}
            </div>
          );
        },
        filterFn: (row, id, value) => value.includes(row.getValue(id))
      });
    });
  }

  columns.push(
    {
      accessorKey: 'request_created',
      filterFn: 'dateBetweenFilterFn',
      header: ({ column }) => <ColumnHeaderCycleSort column={column} title="Created At" />,
      cell: ({ row }) => {
        const isoTime = row.getValue('request_created') as string;

        return (
          <HoverLabelCell
            onClick={() => onOpen(row.original)}
            className="min-w-20"
            label={format(parseISO(isoTime), 'PPpp')}
          >
            {format(parseISO(isoTime), 'MM/dd/yyyy')}
          </HoverLabelCell>
        );
      }
    },
    {
      accessorKey: 'uploaded_at',
      filterFn: 'dateBetweenFilterFn',
      header: ({ column }) => <ColumnHeaderCycleSort column={column} title="Uploaded At" />,
      cell: ({ row }) => {
        const uploadedAt = row.getValue('uploaded_at') as string;

        return (
          <HoverLabelCell
            onClick={() => onOpen(row.original)}
            className="min-w-20"
            label={uploadedAt ? format(parseISO(uploadedAt), 'PPpp') : undefined}
          >
            {uploadedAt ? format(parseISO(uploadedAt), 'MM/dd/yyyy') : ''}
          </HoverLabelCell>
        );
      }
    },
    {
      accessorKey: 'clinic_pet_id',
      header: ({ column }) => <ColumnHeaderCycleSort column={column} title="Pet ID" />,
      cell: ({ row }) => {
        const petId = row.getValue('clinic_pet_id') as string;

        return (
          <HoverLabelCell onClick={() => onOpen(row.original)} className="w-[10ch]" label={petId}>
            <div className="truncate">{petId}</div>
          </HoverLabelCell>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      meta: {
        hideWhenMd: true
      }
    },
    {
      accessorKey: 'pet_name',
      header: ({ column }) => <ColumnHeaderCycleSort column={column} title="Pet Name" />,
      cell: ({ row }) => <div onClick={() => onOpen(row.original)}>{row.getValue('pet_name')}</div>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      }
    },
    {
      accessorKey: 'tags',
      header: ({ column }) => <ColumnHeaderCycleSort column={column} title="Location" />,
      cell: ({ row }) => <TagBadges tags={row.getValue('tags')} tagsToShow={LocationCheckupTags} />,
      filterFn: (row, id, value) => onlyCheckupTags(row.getValue(id)).some((tag) => value.includes(tag)),
      meta: {
        hideWhenMd: true
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }) => {
        return <CheckupActionRowMenu row={row} openQRCodeDialog={openQRCodeDialog} onOpen={onOpen} />;
      }
    }
  );

  return columns;
};
